import { inject, Injectable } from '@angular/core';
import { ELanguage } from '@app/app.interface';
import { TranslateService } from '@ngx-translate/core';
import { LOCALE_MAP } from '@shared/constants/locale.constants';

export type TNumberSeparator = ',' | '.';

@Injectable({ providedIn: 'root' })
export class NumberSeparatorUtilitiesService {
  private readonly translate = inject(TranslateService);

  public getDecimalSeparator(language?: ELanguage | null): TNumberSeparator {
    const defaultLanguage = (this.translate.currentLang as ELanguage) ?? ELanguage.en;

    switch (language ?? defaultLanguage) {
      case ELanguage.de:
        return ',';
      default:
        return '.';
    }
  }

  public getThousandSeparator(language?: ELanguage | null): TNumberSeparator {
    const defaultLanguage = (this.translate.currentLang as ELanguage) ?? ELanguage.en;

    switch (language ?? defaultLanguage) {
      case ELanguage.de:
        return '.';
      default:
        return ',';
    }
  }

  public formatNumberToLocaleString(
    value: number,
    language?: ELanguage | null,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    useGrouping = false,
  ): string {
    return value.toLocaleString(language ?? this.translate.currentLang, {
      maximumFractionDigits,
      minimumFractionDigits,
      useGrouping,
    });
  }

  public formatNumber(value: number | null, language: ELanguage): string {
    if (!value) {
      return '';
    }

    const locale = LOCALE_MAP[language] || LOCALE_MAP[ELanguage.en];

    return value.toLocaleString(locale, { useGrouping: true });
  }

  public formatBigNumberString(value: string, language?: ELanguage): string {
    if (!value) {
      return '';
    }

    const lang = language ?? (this.translate.currentLang as ELanguage) ?? ELanguage.en;

    const [integer, decimal] = value.replace(/[^0-9.,]/g, '').split(/[.,]/);
    const groupSep = this.getThousandSeparator(lang);
    const decimalSep = this.getDecimalSeparator(lang);
    const groupedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);

    return decimal ? `${groupedInteger}${decimalSep}${decimal}` : groupedInteger;
  }

  public normalizeMoneyStringToNumber(value: string, language?: ELanguage | null): number | null {
    if (!value) {
      return null;
    }

    const lang = language ?? (this.translate.currentLang as ELanguage) ?? ELanguage.en;
    const thousandsSep = this.getThousandSeparator(lang);
    const decimalSep = this.getDecimalSeparator(lang);

    let normalized = value.split(thousandsSep).join('');

    if (decimalSep !== '.') {
      normalized = normalized.replace(decimalSep, '.');
    }

    const num = Number(normalized);

    return isNaN(num) ? null : num;
  }

  public parseMoneyValue(value: string | number | null | undefined, language?: ELanguage | null): number | null {
    if (typeof value === 'number') {
      return isNaN(value) ? null : value;
    }

    if (typeof value === 'string' && value) {
      return this.normalizeMoneyStringToNumber(value, language);
    }

    return null;
  }
}
